import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, Plus, X, Check, Loader2, Camera, Compass, Image as ImageIcon, GripVertical } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useToast } from '../ui/use-toast';
import { zonesService } from '../../services/zones';
import { ZONE_EXPOSURES, type Zone, type ZoneExposure } from '../../types/zone';
import { DirectUploadProvider } from '@docflow/react-activestorage-provider';
import { apiClient } from '../../utils/api-client';
import { tokenStorage } from '../../utils/api-client';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface ZoneManagerProps {
  locationSlug: string;
  isStaffMember: boolean;
  locationType?: string;
}

/** Radix Select treats '' as "no value", so "not recorded" carries a token. */
const NO_EXPOSURE = '__none__';

interface SortableZoneItemProps {
  zone: Zone;
  isStaffMember: boolean;
  onEdit: (zone: Zone) => void;
  onDelete: (zone: Zone) => void;
}

function SortableZoneItem({ zone, isStaffMember, onEdit, onDelete }: SortableZoneItemProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: zone.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} className="flex-shrink-0">
      <CardContent className="flex items-center gap-2 p-2">
        {isStaffMember && (
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing touch-none"
            {...attributes}
            {...listeners}
            aria-label="Drag to reorder"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        )}
        {zone.image_small_url && (
          <img
            src={zone.image_small_url}
            alt={zone.name}
            className="w-8 h-8 rounded object-cover shrink-0"
          />
        )}
        <span className="text-sm font-medium">{zone.name}</span>
        {/* Which way the rock faces, on the chip itself — otherwise the only
            way to see what a sector is set to is to open its form. */}
        {zone.exposure && (
          <span className="flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground">
            <Compass className="w-3 h-3" />
            {t(`cragInfo.exposures.${zone.exposure}`)}
          </span>
        )}
        {isStaffMember && (
          <div className="flex gap-1 ml-1">
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground p-0.5"
              onClick={() => onEdit(zone)}
              aria-label={t('zoneManager.editZone')}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              className="text-muted-foreground hover:text-destructive p-0.5"
              onClick={() => onDelete(zone)}
              aria-label={t('zoneManager.deleteZone')}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function ZoneManager({ locationSlug: gymSlug, isStaffMember, locationType = 'gym' }: ZoneManagerProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [zones, setZones] = useState<Zone[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [nameInput, setNameInput] = useState('');
  // Outdoors only: a gym wall has no compass aspect, and neither has a
  // competition's zone.
  const [exposureInput, setExposureInput] = useState<ZoneExposure | ''>('');
  const isCrag = locationType === 'crag';
  const [saving, setSaving] = useState(false);
  const [imageSignedId, setImageSignedId] = useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputCameraRef = useRef<HTMLInputElement>(null);
  const fileInputGalleryRef = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const fetchZones = useCallback(async () => {
    try {
      const data = await zonesService.getZonesForLocation(gymSlug, locationType);
      setZones(data);
    } catch {
      // silently fail - zones are optional
    } finally {
      setLoading(false);
    }
  }, [gymSlug, locationType]);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  function openCreate() {
    setEditingZone(null);
    setNameInput('');
    setExposureInput('');
    setImageSignedId(null);
    setImagePreviewUrl(null);
    setFormOpen(true);
  }

  function openEdit(zone: Zone) {
    setEditingZone(zone);
    setNameInput(zone.name);
    setExposureInput(zone.exposure ?? '');
    setImageSignedId(null);
    setImagePreviewUrl(zone.image_small_url);
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditingZone(null);
    setNameInput('');
    setExposureInput('');
    setImageSignedId(null);
    setImagePreviewUrl(null);
  }

  async function handleSave() {
    if (!nameInput.trim()) return;
    setSaving(true);
    try {
      if (editingZone) {
        const updated = await zonesService.updateZone(gymSlug, editingZone.slug, {
          name: nameInput.trim(),
          // Sent even when blank, so an aspect set by mistake can be cleared.
          ...(isCrag && { exposure: exposureInput }),
          ...(imageSignedId && { image_signed_id: imageSignedId })
        }, locationType);
        setZones(prev => prev.map(z => z.id === updated.id ? updated : z));
        toast({ title: t('zoneManager.updateSuccess') });
      } else {
        const created = await zonesService.createZone(gymSlug, {
          name: nameInput.trim(),
          ...(isCrag && exposureInput ? { exposure: exposureInput } : {}),
          ...(imageSignedId && { image_signed_id: imageSignedId })
        }, locationType);
        setZones(prev => [...prev, created]);
        toast({ title: t('zoneManager.createSuccess') });
      }
      closeForm();
    } catch (err) {
      toast({
        title: editingZone ? t('zoneManager.editZone') : t('zoneManager.addZone'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(zone: Zone) {
    if (!window.confirm(t('zoneManager.deleteConfirm'))) return;
    try {
      await zonesService.deleteZone(gymSlug, zone.slug, locationType);
      setZones(prev => prev.filter(z => z.id !== zone.id));
      toast({ title: t('zoneManager.deleteSuccess') });
    } catch (err) {
      toast({
        title: t('zoneManager.deleteZone'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive'
      });
    }
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = zones.findIndex(z => z.id === active.id);
    const newIndex = zones.findIndex(z => z.id === over.id);
    const reordered = arrayMove(zones, oldIndex, newIndex);
    setZones(reordered); // optimistic update

    try {
      const updated = await zonesService.reorderZones(gymSlug, reordered.map(z => z.id), locationType);
      setZones(updated);
    } catch {
      setZones(zones); // revert on error
      toast({ title: t('zoneManager.reorderError', { defaultValue: 'Failed to save order' }), variant: 'destructive' });
    }
  }

  const getApiOrigin = useCallback(() => {
    const baseURL = apiClient.getBaseURL();
    if (!baseURL) {
      return {
        host: window.location.hostname,
        port: window.location.port || undefined,
        protocol: window.location.protocol.replace(':', '')
      };
    }
    try {
      const url = new URL(baseURL);
      return {
        host: url.hostname,
        port: url.port || undefined,
        protocol: url.protocol.replace(':', '')
      };
    } catch {
      return {
        host: window.location.hostname,
        port: window.location.port || undefined,
        protocol: window.location.protocol.replace(':', '')
      };
    }
  }, []);

  if (loading) return null;
  if (!isStaffMember && zones.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold">{t('zoneManager.title')}</h3>
        {isStaffMember && !formOpen && (
          <Button size="sm" variant="outline" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1" />
            {t('zoneManager.addZone')}
          </Button>
        )}
      </div>

      {/* Zone list with drag-and-drop */}
      {zones.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={zones.map(z => z.id)} strategy={verticalListSortingStrategy}>
            <div className="flex flex-wrap gap-2 mb-3">
              {zones.map(zone => (
                <SortableZoneItem
                  key={zone.id}
                  zone={zone}
                  isStaffMember={isStaffMember}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Create/Edit Form */}
      {isStaffMember && formOpen && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-sm">
              {editingZone ? t('zoneManager.editZone') : t('zoneManager.addZone')}
            </h4>
            <button type="button" onClick={closeForm} className="text-muted-foreground hover:text-foreground">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="zone-name" className="mb-1 block text-sm">{t('zoneManager.zoneName')}</Label>
              <Input
                id="zone-name"
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                placeholder={t('zoneManager.zoneName')}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSave(); } }}
              />
            </div>
            {/* Which way this piece of rock faces, and so when it is in the
                sun. Only outdoors: a gym wall has no compass aspect. */}
            {isCrag && (
              <div>
                <Label htmlFor="zone-exposure" className="mb-1 block text-sm">
                  {t('cragAdmin.routes.exposure')}
                </Label>
                <Select
                  value={exposureInput || NO_EXPOSURE}
                  onValueChange={(next) =>
                    setExposureInput(next === NO_EXPOSURE ? '' : (next as ZoneExposure))
                  }
                >
                  <SelectTrigger id="zone-exposure">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_EXPOSURE}>{t('cragAdmin.routes.noExposure')}</SelectItem>
                    {ZONE_EXPOSURES.map(exposure => (
                      <SelectItem key={exposure} value={exposure}>
                        {t(`cragInfo.exposures.${exposure}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="mb-1 block text-sm">{t('common.image', { defaultValue: 'Image' })}</Label>
              <div className="flex items-center gap-3">
                {imagePreviewUrl && (
                  <img
                    src={imagePreviewUrl}
                    alt="preview"
                    className="w-12 h-12 rounded object-cover border"
                  />
                )}
                <DirectUploadProvider
                  origin={getApiOrigin()}
                  headers={{ Authorization: `Bearer ${tokenStorage.getToken() || ''}` }}
                  onSuccess={(signedIds: string[]) => {
                    if (!signedIds || signedIds.length === 0) {
                      setUploadingImage(false);
                      toast({ title: t('climbCreate.imageStep.uploadError'), variant: 'destructive' });
                      return;
                    }
                    setImageSignedId(signedIds[0]);
                    setUploadingImage(false);
                  }}
                  render={({ handleUpload }: { handleUpload: (files: FileList) => void }) => {
                    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
                      const files = e.target.files;
                      if (!files || files.length === 0) return;
                      setUploadingImage(true);
                      setImagePreviewUrl(URL.createObjectURL(files[0]));
                      handleUpload(files);
                      e.target.value = '';
                    };
                    return (
                      <>
                        <input ref={fileInputCameraRef} type="file" accept="image/*" capture="environment" className="sr-only" onChange={handleFileSelect} />
                        <input ref={fileInputGalleryRef} type="file" accept="image/*" className="sr-only" onChange={handleFileSelect} />
                        <Button type="button" variant="outline" size="sm" disabled={uploadingImage} onClick={() => fileInputCameraRef.current?.click()}>
                          {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-1" />{t('zoneManager.camera')}</>}
                        </Button>
                        <Button type="button" variant="outline" size="sm" disabled={uploadingImage} onClick={() => fileInputGalleryRef.current?.click()}>
                          <ImageIcon className="w-4 h-4 mr-1" />{t('zoneManager.gallery')}
                        </Button>
                      </>
                    );
                  }}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={closeForm}>
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleSave}
                disabled={saving || !nameInput.trim() || uploadingImage}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                {t('zoneManager.saveZone')}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
